import apiClient from './apiClient';
import { saveOffline, getOfflineData, deleteOfflineData, STORES } from '../utils/offlineDB';

// Helper: File zu Base64 konvertieren
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

// Helper: Base64 zurück zu File konvertieren
const base64ToFile = (base64, filename, mimeType) => {
  const byteString = atob(base64.split(',')[1]);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new File([ab], filename, { type: mimeType });
};

// Upload mit Offline-Support
export const uploadFoto = async (kundeId, file, beschreibung = '', anfrageId = null) => {
  try {
    if (navigator.onLine) {
      const formData = new FormData();
      formData.append('foto', file);
      formData.append('kundeId', kundeId);
      if (beschreibung) formData.append('beschreibung', beschreibung);
      if (anfrageId) formData.append('anfrageId', anfrageId);

      const response = await apiClient.post('/fotos/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    }

    // Offline: File als Base64 speichern
    console.log('📴 Foto offline gespeichert');
    const base64 = await fileToBase64(file);
    const offlineId = await saveOffline(STORES.FOTOS, {
      kundeId,
      anfrageId,
      beschreibung,
      base64,
      filename: file.name,
      mimeType: file.type,
      size: file.size,
      offlineId: 'offline-' + Date.now()
    });

    return {
      message: 'Foto offline gespeichert - wird synchronisiert',
      offline: true,
      foto: {
        id: 'offline-' + offlineId,
        kundeId,
        anfrageId,
        beschreibung,
        dateiname: file.name,
        dateipfad: 'offline',
        erstelltAm: new Date().toISOString(),
        offline: true,
        // Base64 Preview für sofortige Anzeige
        previewUrl: base64
      }
    };

  } catch (error) {
    // Bei Netzwerk-Fehler: Offline speichern
    if (error.code === 'ERR_NETWORK' || !navigator.onLine) {
      console.log('📴 Foto offline gespeichert (Fehler)');
      const base64 = await fileToBase64(file);
      const offlineId = await saveOffline(STORES.FOTOS, {
        kundeId,
        anfrageId,
        beschreibung,
        base64,
        filename: file.name,
        mimeType: file.type,
        size: file.size,
        offlineId: 'offline-' + Date.now()
      });

      return {
        message: 'Foto offline gespeichert',
        offline: true,
        foto: {
          id: 'offline-' + offlineId,
          kundeId,
          anfrageId,
          beschreibung,
          dateiname: file.name,
          dateipfad: 'offline',
          erstelltAm: new Date().toISOString(),
          offline: true,
          previewUrl: base64
        }
      };
    }
    throw error;
  }
};

export const getFotosByKundeId = async (kundeId) => {
  const response = await apiClient.get(`/fotos/kunde/${kundeId}`);
  return response.data;
};

export const getFotoUrl = (foto) => {
  // Wenn Offline-Foto mit previewUrl
  if (foto.previewUrl) {
    return foto.previewUrl;
  }

  // Base URL ohne /api (z.B. http://localhost:3000)
  const baseURL = (import.meta.env.VITE_API_URL || 'http://localhost:3000/api').replace('/api', '');

  // Wenn dateipfad bereits mit /api/uploads beginnt, direkt verwenden
  if (foto.dateipfad?.startsWith('/api/uploads')) {
    return `${baseURL}${foto.dateipfad}`;
  }

  // Fallback für alte Fotos: nur Dateiname extrahieren
  const filename = foto.dateipfad?.split('/').pop() || foto.dateiname;
  return `${baseURL}/api/uploads/${filename}`;
};

export const updateFoto = async (fotoId, data) => {
  const response = await apiClient.put(`/fotos/${fotoId}`, data);
  return response.data;
};

export const deleteFoto = async (fotoId) => {
  const response = await apiClient.delete(`/fotos/${fotoId}`);
  return response.data;
};

// NEU: Sync pending fotos
export const syncOfflineData = async () => {
  if (!navigator.onLine) {
    console.log('Noch offline - kann nicht synchronisieren');
    return { synced: 0, failed: 0 };
  }

  try {
    const pendingData = await getOfflineData(STORES.FOTOS);
    let synced = 0;
    let failed = 0;

    for (const item of pendingData) {
      try {
        const data = item.data;

        // Base64 zurück zu File konvertieren
        const file = base64ToFile(data.base64, data.filename, data.mimeType);

        // FormData erstellen
        const formData = new FormData();
        formData.append('foto', file);
        formData.append('kundeId', data.kundeId);
        if (data.beschreibung) formData.append('beschreibung', data.beschreibung);
        if (data.anfrageId) formData.append('anfrageId', data.anfrageId);

        await apiClient.post('/fotos/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        await deleteOfflineData(STORES.FOTOS, item.id);
        synced++;
        console.log('✅ Foto synchronisiert:', item.id);
      } catch (error) {
        failed++;
        console.error('❌ Foto sync failed:', item.id, error.message);
      }
    }

    return { synced, failed, total: pendingData.length };
  } catch (error) {
    console.error('Offline sync error:', error);
    return { synced: 0, failed: 0, error: error.message };
  }
};

// Anzahl der ausstehenden Offline-Fotos
export const getPendingOfflineCount = async () => {
  try {
    const pendingData = await getOfflineData(STORES.FOTOS);
    return pendingData.length;
  } catch (error) {
    return 0;
  }
};

// Offline-Fotos für einen Kunden laden (für Anzeige)
export const getOfflineFotosForKunde = async (kundeId) => {
  try {
    const pendingData = await getOfflineData(STORES.FOTOS);
    return pendingData
      .filter(item => item.data.kundeId === kundeId)
      .map(item => ({
        id: 'offline-' + item.id,
        kundeId: item.data.kundeId,
        anfrageId: item.data.anfrageId,
        beschreibung: item.data.beschreibung,
        dateiname: item.data.filename,
        dateipfad: 'offline',
        erstelltAm: new Date(item.timestamp).toISOString(),
        offline: true,
        previewUrl: item.data.base64
      }));
  } catch (error) {
    console.error('getOfflineFotosForKunde error:', error);
    return [];
  }
};

// API-Objekt für einheitlichen Export
export const fotoApi = {
  upload: uploadFoto,
  getByKundeId: getFotosByKundeId,
  getUrl: getFotoUrl,
  update: updateFoto,
  delete: deleteFoto,
  syncOfflineData,
  getPendingOfflineCount,
  getOfflineFotosForKunde
};

export default fotoApi;
